import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './i18n';
import Landing from './pages/Landing';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
// import ProfileSetup from './pages/ProfileSetup'; // No longer needed - users skip initial setup
import GamesList from './pages/GamesList';
import GameDetail from './pages/GameDetail';
import GamePlay from './pages/GamePlay';
import NewGame from './pages/NewGame';
import Profile from './pages/Profile';
import ClubsList from './pages/ClubsList';
import CreateClub from './pages/CreateClub';
import ClubManagement from './pages/ClubManagement';
import TournamentManagement from './pages/TournamentManagement';
import TournamentDetail from './pages/TournamentDetail';
import TournamentBracketFullscreen from './pages/TournamentBracketFullscreen';
import Invitations from './pages/Invitations';
import GlobalAdmin from './pages/GlobalAdmin';
import Training from './pages/Training';
import Statistics from './pages/Statistics';
import { TableRegistration } from './pages/TableRegistration';
import { TableView } from './pages/TableView';
import Loading from './components/Loading';
import Navigation from './components/Navigation';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, needsProfileSetup } = useAuth();

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/signin" replace />;
  if (needsProfileSetup) return <Navigate to="/profile-setup" replace />;

  return <>{children}</>;
};

// Component to conditionally render Navigation based on current route
const AppContent: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Define paths that should NOT show navigation (standalone pages)
  const standaloneRoutes = ['/table-registration', '/table-view'];
  const isTableMode = sessionStorage.getItem('table_mode') === 'true';
  const shouldShowNavigation = user && !standaloneRoutes.some(route => location.pathname.startsWith(route)) && !isTableMode;

  return (
    <div className="App">
      {/* Conditionally render Navigation */}
      {shouldShowNavigation && <Navigation />}
      
      <main className="main-content">
        <Routes>
            <Route path="/" element={!user ? <Landing /> : <Navigate to="/games" replace />} />
            <Route path="/signin" element={!user ? <SignIn /> : <Navigate to="/games" replace />} />
            <Route path="/signup" element={!user ? <SignUp /> : <Navigate to="/games" replace />} />
            {/* ProfileSetup route removed - users now skip initial setup and see profile completion hints */}
            
            <Route path="/games" element={
              <ProtectedRoute>
                <GamesList />
              </ProtectedRoute>
            } />
            
            <Route path="/game/:id" element={
              <ProtectedRoute>
                <GameDetail />
              </ProtectedRoute>
            } />
            
            <Route path="/play/:id" element={
              <ProtectedRoute>
                <GamePlay />
              </ProtectedRoute>
            } />
            
            <Route path="/new-game" element={
              <ProtectedRoute>
                <NewGame />
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            
            <Route path="/clubs" element={
              <ProtectedRoute>
                <ClubsList />
              </ProtectedRoute>
            } />
            
            <Route path="/clubs/create" element={
              <ProtectedRoute>
                <CreateClub />
              </ProtectedRoute>
            } />
            
            <Route path="/clubs/:id" element={
              <ProtectedRoute>
                <ClubManagement />
              </ProtectedRoute>
            } />
            
        <Route path="/clubs/:id/manage" element={
          <ProtectedRoute>
            <ClubManagement />
          </ProtectedRoute>
        } />
        
        <Route path="/clubs/:clubId/tournaments" element={
          <ProtectedRoute>
            <TournamentManagement />
          </ProtectedRoute>
        } />
        
        <Route path="/clubs/:clubId/tournaments/:tournamentId" element={
          <ProtectedRoute>
            <TournamentDetail />
          </ProtectedRoute>
        } />
        
        <Route path="/tournaments/:id/bracket" element={
          <ProtectedRoute>
            <TournamentBracketFullscreen />
          </ProtectedRoute>
        } />
        
        <Route path="/invitations" element={
          <ProtectedRoute>
            <Invitations />
          </ProtectedRoute>
        } />
        
        <Route path="/global-admin" element={
          <ProtectedRoute>
            <GlobalAdmin />
          </ProtectedRoute>
        } />
        
        <Route path="/training" element={
          <ProtectedRoute>
            <Training />
          </ProtectedRoute>
        } />
        
        <Route path="/statistics" element={
          <ProtectedRoute>
            <Statistics />
          </ProtectedRoute>
        } />

        {/* PUBLIC ROUTES (no authentication needed) */}
        <Route path="/table-registration" element={<TableRegistration />} />
        <Route path="/table-view" element={<TableView />} />
            
        <Route path="/" element={<Navigate to="/games" replace />} />
          </Routes>
        </main>
      </div>
  );
};

const AppRoutes: React.FC = () => {
  const { loading } = useAuth();

  if (loading) return <Loading />;

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;